import React, { forwardRef, useState } from 'react';
import { useSettings } from '../contexts/SettingsContext';

const InputBox = forwardRef(({
  label,
  type = 'text',
  placeholder,
  value,
  onChange,
  onBlur,
  onFocus,
  error,
  helperText,
  disabled = false,
  required = false,
  className = '',
  id,
  name,
  autoComplete,
  ...props
}, ref) => {

  const { settings } = useSettings();
  const primaryColor = settings.theme?.colors?.primary || '#3B5BDB';
  const secondaryColor = settings.theme?.colors?.secondary || '#20C997';

  const [showPassword, setShowPassword] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [isAutofilled, setIsAutofilled] = useState(false);
  const blurTimeoutRef = React.useRef(null);
  const inputRef = React.useRef(null);

  // Track whether the input has a value (typed, pasted, autofilled, or controlled)
  const hasValue = Boolean(value) || Boolean(inputRef.current?.value);

  // Merge forwarded ref with internal ref
  React.useImperativeHandle(ref, () => inputRef.current);

  const inputId = id || name || label?.toLowerCase().replace(/\s+/g, '-');

  // Detect browser autofill via the animationstart event
  // Browsers add a special animation when autofill is applied
  React.useEffect(() => {
    const input = inputRef.current;
    if (!input) return;

    const handleAnimationStart = (e) => {
      // Chrome triggers an animation named 'onautofillstart' (or similar) on autofill
      if (e.animationName === 'onAutoFillStart') {
        setIsAutofilled(true);
        setIsFocused(true);
      } else if (e.animationName === 'onAutoFillCancel') {
        setIsAutofilled(false);
      }
    };

    // Fallback: detect autofill by checking pseudo-class after a short delay
    // Some browsers don't fire animationstart reliably
    const autofillCheckTimer = setTimeout(() => {
      try {
        if (input.matches(':-webkit-autofill')) {
          setIsAutofilled(true);
        }
      } catch {
        // matches() may throw for unsupported selectors
      }
    }, 600);

    input.addEventListener('animationstart', handleAnimationStart);
    return () => {
      input.removeEventListener('animationstart', handleAnimationStart);
      clearTimeout(autofillCheckTimer);
    };
  }, []);

  const handleFocus = (e) => {
    // Clear any pending blur timeout
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
      blurTimeoutRef.current = null;
    }
    setIsFocused(true);
    onFocus?.(e);
  };

  const handleBlur = (e) => {
    // Delay blur to allow autofill dropdown interaction
    // The browser fires blur when user hovers the autofill popup
    blurTimeoutRef.current = setTimeout(() => {
      // Only unfocus if the input is truly not focused anymore
      if (inputRef.current && document.activeElement !== inputRef.current) {
        setIsFocused(false);
        // Keep isAutofilled true if the input still has a value (autofill persists after blur)
        if (!inputRef.current?.value) {
          setIsAutofilled(false);
        }
      }
      blurTimeoutRef.current = null;
    }, 500);
    onBlur?.(e);
  };

  // Clean up timeout on unmount
  React.useEffect(() => {
    return () => {
      if (blurTimeoutRef.current) {
        clearTimeout(blurTimeoutRef.current);
      }
    };
  }, []);

  const inputType = type === 'password' && showPassword ? 'text' : type;

  const EyeIcon = () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
        d="M15 12c0 1.654-1.346 3-3 3s-3-1.346-3-3 1.346-3 3-3 3 1.346 3 3z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
        d="M12 5C5.636 5 2 12 2 12s3.636 7 10 7 10-7 10-7-3.636-7-10-7z" />
    </svg>
  );

  const EyeOffIcon = () => (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" stroke="currentColor">
      <path d="M2.50004 2.5L5.49101 5.49097M17.5 17.5L14.5094 14.5093M11.5624 15.6872C11.0565 15.7831 10.5343 15.8333 10.0004 15.8333C6.26905 15.8333 3.11046 13.3809 2.04858 9.99997C2.33767 9.07958 2.78213 8.22801 3.35124 7.47598M8.23227 8.23223C8.68468 7.77982 9.30968 7.5 10 7.5C11.3808 7.5 12.5 8.61929 12.5 10C12.5 10.6904 12.2202 11.3154 11.7678 11.7678M8.23227 8.23223L11.7678 11.7678M8.23227 8.23223L5.49101 5.49097M11.7678 11.7678L5.49101 5.49097M11.7678 11.7678L14.5094 14.5093M5.49101 5.49097C6.79086 4.65295 8.33884 4.16667 10.0004 4.16667C13.7318 4.16667 16.8904 6.61909 17.9522 10C17.3632 11.8756 16.1288 13.4654 14.5094 14.5093" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );

  // Whether gradient border should show
  const showGradientBorder = (isFocused || isAutofilled) && !error;

  return (
    <div className={`w-full ${className}`}>
      {label && (
        <label
          htmlFor={inputId}
          className="block text-sm mb-2 text-slate-600 dark:text-slate-400"
        >
          {label}
        </label>
      )}

      {/* Wrapper div acts as gradient border — browser autofill cannot override wrapper styles */}
      <div
        className="relative"
        style={
          showGradientBorder
            ? {
                padding: '1px',
                borderRadius: '1rem',
                background: `linear-gradient(135deg, ${primaryColor} 25%, ${secondaryColor} 75%)`,
              }
            : {
                padding: '1px',
                borderRadius: '1rem',
                background: 'var(--input-border, #E5E7EB)',
              }
        }
      >
        <input
          ref={inputRef}
          id={inputId}
          name={name}
          type={inputType}
          value={value}
          onChange={onChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          placeholder={placeholder}
          disabled={disabled}
          required={required}
          autoComplete={autoComplete}
          className={`
            w-full h-12 px-4
            text-gray-900 dark:text-gray-100
            placeholder-gray-400 dark:placeholder-gray-500
            focus:outline-none
            transition-all duration-200
            ${type === 'password' ? 'pr-12' : ''}
            ${disabled ? 'opacity-60 cursor-not-allowed' : ''}
          `}
          style={{
            borderRadius: 'calc(1rem - 1px)',
            border: 'none',
            backgroundColor: 'var(--input-bg, #fff)',
            color: 'var(--input-text, #111827)',
            outline: 'none',
            boxShadow: 'none',
            appearance: 'none',
          }}
          {...props}
        />

        {type === 'password' && (
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 transition-colors duration-200 pointer-events-auto"
          >
            {showPassword ? <EyeIcon /> : <EyeOffIcon />}
          </button>
        )}
      </div>

      {(error || helperText) && (
        <p className={`mt-1.5 text-sm ${error ? 'text-red-500' : 'text-gray-500 dark:text-gray-400'}`}>
          {error || helperText}
        </p>
      )}
    </div>
  );
});

InputBox.displayName = 'InputBox';
export default InputBox;
